/**
 * Payment Gateway Script Loader
 * Dynamically loads payment gateway SDKs when needed
 */

// Track loaded scripts to avoid duplicate loading
const loadedScripts = {};

/**
 * Load external script dynamically
 * @param {string} src - Script URL
 * @param {string} id - Unique script identifier
 * @returns {Promise} Resolves when script is loaded
 */
export const loadScript = (src, id) => {
  return new Promise((resolve, reject) => {
    // Check if script is already loaded
    if (loadedScripts[id]) {
      resolve();
      return;
    }

    // Check if script tag already exists
    const existingScript = document.getElementById(id);
    if (existingScript) {
      loadedScripts[id] = true;
      resolve();
      return;
    }

    // Create new script tag
    const script = document.createElement('script');
    script.src = src;
    script.id = id;
    script.async = true;

    script.onload = () => {
      loadedScripts[id] = true;
      resolve();
    };

    script.onerror = () => {
      reject(new Error(`Failed to load script: ${src}`));
    };

    document.body.appendChild(script);
  });
};

/**
 * Load Razorpay checkout script
 * @returns {Promise}
 */
export const loadRazorpay = () => {
  return loadScript('https://checkout.razorpay.com/v1/checkout.js', 'razorpay-checkout');
};

/**
 * Load Flutterwave checkout script
 * @returns {Promise}
 */
export const loadFlutterwave = () => {
  return loadScript('https://checkout.flutterwave.com/v3.js', 'flutterwave-checkout');
};

/**
 * Load Paystack inline script
 * @returns {Promise}
 */
export const loadPaystack = () => {
  return loadScript('https://js.paystack.co/v1/inline.js', 'paystack-inline');
};

/**
 * Load Stripe.js
 * @returns {Promise}
 */
export const loadStripe = () => {
  return loadScript('https://js.stripe.com/v3/', 'stripe-js');
};

/**
 * Check if payment gateway is available
 * @param {string} gateway - Gateway name (Razorpay, FlutterwaveCheckout, PaystackPop, Stripe)
 * @returns {boolean}
 */
export const isGatewayAvailable = (gateway) => {
  return typeof window !== 'undefined' && typeof window[gateway] !== 'undefined';
};

/**
 * Wait for gateway to be available
 * @param {string} gateway - Gateway name
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise}
 */
export const waitForGateway = (gateway, timeout = 5000) => {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();

    const checkGateway = () => {
      if (isGatewayAvailable(gateway)) {
        resolve();
      } else if (Date.now() - startTime > timeout) {
        reject(new Error(`Timeout waiting for ${gateway} to load`));
      } else {
        setTimeout(checkGateway, 100);
      }
    };

    checkGateway();
  });
};

/**
 * Remove script from DOM
 * @param {string} id - Script identifier
 */
export const removeScript = (id) => {
  const script = document.getElementById(id);
  if (script) {
    script.remove();
    delete loadedScripts[id];
  }
};
